import { useQuery } from "@tanstack/react-query";
import { SearchIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { DialogTrigger, Separator } from "react-aria-components";

import { AccessibleIcon } from "@/components/accessible-icon";
import { Button } from "@/components/button";
import { Dialog } from "@/components/dialog";
import { MenuButton } from "@/components/menu";
import { Popover } from "@/components/popover";
import { SearchField, SearchInput } from "@/components/search-field";
import { useDebounce } from "@/hooks/useDebounce";
import apiClient from "@/lib/api/client";

import type {
  GlobalFileSearchResponse,
  GlobalSearchBody,
  GlobalSearchResponse,
} from "./types";

import { SearchLoadingSkeleton } from "./components/SearchLoadingSkeleton";
import { SearchNoResults } from "./components/SearchNoResults";
import { SearchResultSection } from "./components/SearchResultSection";

function useGlobalSearch(query: string) {
  return useQuery({
    enabled: Boolean(query),
    queryFn: async () =>
      await apiClient
        .url("/search")
        .post({ key: query } as GlobalSearchBody)
        .json<Omit<GlobalSearchResponse, "myFiles">>(),
    queryKey: ["search", query],
  });
}

function useGlobalFileSearch(query: string) {
  return useQuery({
    enabled: Boolean(query),
    queryFn: async () =>
      await apiClient
        .url("/search/files")
        .post({ key: query } as GlobalSearchBody)
        .json<GlobalFileSearchResponse>(),
    queryKey: ["search", "files", query],
  });
}

export function Search() {
  const [inputValue, setInputValue] = useState("");
  const debouncedQuery = useDebounce(inputValue);

  const { data: searchData, isFetching } = useGlobalSearch(debouncedQuery);
  const { data: fileSearchData, isFetching: isFetchingFiles } =
    useGlobalFileSearch(debouncedQuery);
  const data: GlobalSearchResponse | undefined = searchData && {
    ...searchData,
    myFiles: fileSearchData?.myFiles ?? [],
  };

  const handleSearch = (value: string) => {
    if (value.trim()) {
      setInputValue(value);
    } else {
      setInputValue("");
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setInputValue("");
    }
  };

  const hasResults = data && Object.values(data).some((r) => r.length > 0);

  return (
    <DialogTrigger onOpenChange={handleOpenChange}>
      <>
        <MenuButton
          className="text-nav-item-button-icon-fg"
          isIconOnly
          noIndicator
          size="sm"
          variant="tertiary"
        >
          <AccessibleIcon>
            <SearchIcon />
          </AccessibleIcon>
        </MenuButton>
        <Popover className="!max-h-[min(728px,80dvh)] w-[calc(100vw-16px)] md:w-[480px] overflow-hidden bg-bg-primary shadow-none">
          <Dialog className="flex h-full flex-col">
            {({ close }) => (
              <div className="flex min-h-0 flex-1 flex-col pb-md">
                <div className="flex items-center justify-between px-3xl pt-3xl pb-2xl">
                  <span className="text-lg font-semibold text-text-secondary">
                    Global search
                  </span>
                  <Button onPress={close} variant="unstyled">
                    <AccessibleIcon>
                      <XIcon className="text-text-primary" size={24} />
                    </AccessibleIcon>
                  </Button>
                </div>
                <SearchField className="px-3xl pb-3xl">
                  <SearchInput
                    className="flex items-end gap-xl"
                    onChange={(e) => handleSearch(e.target.value)}
                    onClear={() => setInputValue("")}
                    placeholder="Search..."
                    value={inputValue}
                  />
                </SearchField>

                {debouncedQuery && isFetching && (
                  <>
                    <Separator className="w-full border-t border-border-secondary" />
                    <SearchLoadingSkeleton />
                  </>
                )}

                {debouncedQuery &&
                  !isFetching &&
                  !isFetchingFiles &&
                  data &&
                  !hasResults && (
                    <>
                      <Separator className="w-full border-t border-border-secondary" />
                      <SearchNoResults query={debouncedQuery} />
                    </>
                  )}

                {!isFetching && (hasResults || isFetchingFiles) && data && (
                  <div className="flex min-h-0 flex-1 flex-col">
                    <Separator className="w-full border-t border-border-secondary" />
                    <div className="divide-y divide-border-secondary overflow-y-auto">
                      <SearchResultSection
                        results={data.requests}
                        title="Requests"
                        type="requests"
                      />
                      <SearchResultSection
                        results={data.brands}
                        title="Brands"
                        type="brands"
                      />
                      <SearchResultSection
                        results={data.users}
                        title="Users"
                        type="users"
                      />
                      <SearchResultSection
                        results={data.projects}
                        title="Accounts"
                        type="projects"
                      />
                      {isFetchingFiles ? (
                        <SearchLoadingSkeleton />
                      ) : (
                        <SearchResultSection
                          results={data.myFiles}
                          title="Files"
                          type="myFiles"
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Dialog>
        </Popover>
      </>
    </DialogTrigger>
  );
}
